import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import {
    MEDIUM_PLAIN_TEXT_LENGTH,
    SHORT_PLAIN_TEXT_LENGTH
} from '../../../constants/common.constants';
import { IAccountGroupCreateDTO, IAccountGroupEditDTO } from '../../../api/account-groups.dao';
import { IAccountDetailsDTO } from '../../../api/accounts.dao';

const _assignedAccountGroupIds = Symbol('initialAssignedAccountGroupIds');

class AccountGroupsFormDataVM {
    public defaultValues = {
        name: '',
        description: ''
    };

    @mobx.observable
    public name: string = this.defaultValues.name;

    @mobx.observable
    public description: string = this.defaultValues.description;

    @mobx.observable
    public accounts: IAccountDetailsDTO[] = [];

    @mobx.observable
    private isFormChecked: boolean;

    @mobx.observable
    public serverErrorMessage: string;

    @mobx.observable
    public validationError: string[];

    @mobx.action
    public setName = (name: string) => {
        this.serverErrorMessage = '';
        this.name = name;
    };

    @mobx.action
    public setDescription = (description: string) => {
        this.serverErrorMessage = '';
        this.description = description;
    };

    @mobx.action
    public setAccountGroup = (groupDetails: IAccountGroupCreateDTO): void => {
        this.name = groupDetails.name || this.defaultValues.name;
        this.description = groupDetails.description || this.defaultValues.description;
        this.accounts = groupDetails.accounts;
        this.setInitialAssignedAccountGroupIds(groupDetails.accounts);
    };

    public getNameError(intl: IntlShape): string | null {
        const trimmedName = this.name.trim();

        if (!trimmedName) {
            return intl.formatMessage({
                id: `oe.common.nameIsRequired`,
                defaultMessage: 'Name is required',
                description: 'Error message in account group popover'
            });
        } else if (trimmedName?.length > SHORT_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.validation.shortPlainTextTooLong`,
                defaultMessage: 'Too long - max length is 255 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    public getDescriptionError(intl: IntlShape): string | null {
        if (this.description?.length > MEDIUM_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.validation.mediumPlainTextTooLong`,
                defaultMessage: 'Too long - max length is 2048 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    public isFormValid(intl: IntlShape): boolean {
        return !this.getNameError(intl) && !this.getDescriptionError(intl);
    }

    @mobx.action
    public allowToShowValidationErrors(): void {
        this.isFormChecked = true;
    }

    public showValidationErrors(): boolean {
        return this.isFormChecked;
    }

    public toApiEditGroupDTO(): IAccountGroupEditDTO {
        return {
            name: this.name.trim(),
            description: this.description.trim(),
            addedAccounts: this.getAddedAccountGroupIds(),
            removedAccounts: this.getRemovedAccountGroupIds()
        };
    }

    public toApiCreateGroupDTO(): IAccountGroupCreateDTO {
        return {
            name: this.name.trim(),
            description: this.description.trim(),
            accounts: this.accounts
        };
    }

    @mobx.action
    public setAccounts = (accounts: IAccountDetailsDTO[]) => {
        this.accounts = accounts;
    };

    @mobx.action
    public removeAccount = (accountId: number) => {
        this.accounts = this.accounts.filter(account => account.accountId !== accountId);
    };

    public get assignedAccountGroupIds(): number[] {
        return this.accounts.map(account => account.accountId);
    }

    private [_assignedAccountGroupIds] = new Set<number>();

    public setInitialAssignedAccountGroupIds(accountGroups: IAccountDetailsDTO[]): void {
        if (accountGroups) accountGroups.forEach(accountGroup => this[_assignedAccountGroupIds].add(accountGroup.accountId));
    }
    private getAddedAccountGroupIds(): number[] {
        return this.assignedAccountGroupIds
            .filter(id => !this[_assignedAccountGroupIds].has(id));
    }

    private getRemovedAccountGroupIds(): number[] {
        return [...this[_assignedAccountGroupIds]]
            .filter(id => !this.assignedAccountGroupIds.includes(id));
    }

}

export { AccountGroupsFormDataVM };
